import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SelectPersonModal from './SelectPersonModal'
import tenantApi from '../api/tenant/TenantApi'
import { PersonLiteDto } from '../api/person/models/PersonDto'
import personApi from '../api/person/PersonApi'
import Button from './Button'
import PaginationControls from './common/PaginationControls'
import { calculatePaginationState } from '../utils/paginationUtils'
import { formatPersonName } from '../utils/personUtils'
import Note from '../uiV2/common/note/Note'
import { NoteType } from '../enums/NoteType'
import { toast } from 'sonner'
import TagsInput from '../uiV2/common/form/inputs/TagsInput'
import { JobFunctionBadge } from '../uiV2/helixSearchV2Copy/searchFilters/FilterBadges'
import { JOB_FUNCTION_VALUES } from '../uiV2/helixSearchV2Copy/searchFilters/filterTypes'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200]
const DEFAULT_PAGE_SIZE = 50

const normalizeDepartmentValue = (value?: string | null): string | null => {
    if (!value) {
        return null
    }

    const replacedAmpersand = value.replace(/&/g, 'and')
    const normalizedSpaces = replacedAmpersand
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .split(/\s+/)
        .filter(Boolean)
        .join(' ')

    return normalizedSpaces || null
}

const comparePersonsByName = (
    personA: PersonLiteDto,
    personB: PersonLiteDto,
): number => {
    const nameA = formatPersonName(personA).trim()
    const nameB = formatPersonName(personB).trim()

    if (nameA && nameB) {
        const comparison = nameA.localeCompare(nameB, undefined, {
            sensitivity: 'base',
        })
        if (comparison !== 0) {
            return comparison
        }
    } else if (nameA) {
        return -1
    } else if (nameB) {
        return 1
    }

    return personA.id.localeCompare(personB.id)
}

type Props = {
    currentNetworkLinksIds: string[]
    onClose: (newIds: string[], confirmed: boolean) => void
    onLiveSelectChange: (person: PersonLiteDto, selected: boolean) => void
    onAddFromDepartments?: (personIds: string[]) => Promise<void> | void
}

function AddNetworkLinksModal({
    currentNetworkLinksIds: currentNetworkLinksIds,
    onClose,
    onLiveSelectChange,
    onAddFromDepartments,
}: Props): React.JSX.Element {
    const initialNetworkLinksIdsRef = useRef([...currentNetworkLinksIds])
    const [availableNetworkLinkIds, setAvailableNetworkLinkIds] = useState<
        string[]
    >([])
    const [personsByPage, setPersonsByPage] = useState<
        Record<number, PersonLiteDto[]>
    >({})
    const [currentPage, setCurrentPage] = useState(1)
    const [nonSearchPage, setNonSearchPage] = useState(1)
    const [isPageLoading, setIsPageLoading] = useState<boolean>(false)
    const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([])
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchResults, setSearchResults] = useState<PersonLiteDto[]>([])
    const [isSearchLoading, setIsSearchLoading] = useState(false)
    const [selectedDepartments, setSelectedDepartments] = useState<string[]>([])
    const [isDepartmentLoading, setIsDepartmentLoading] = useState(false)
    const [departmentAddNote, setDepartmentAddNote] = useState<string | null>(
        null,
    )

    const availableIdsSet = useMemo(
        () => new Set(availableNetworkLinkIds),
        [availableNetworkLinkIds],
    )

    const normalizedSearchTerm = searchTerm.trim()
    const isUsingSearch = normalizedSearchTerm.length > 0
    const wasUsingSearchRef = useRef(false)
    const currentPageRef = useRef(currentPage)
    useEffect(() => {
        currentPageRef.current = currentPage
    }, [currentPage])
    const lastSearchTermRef = useRef('')
    const totalResultCount = isUsingSearch
        ? searchResults.length
        : availableNetworkLinkIds.length
    const { totalPages } = useMemo(
        () => calculatePaginationState(totalResultCount, currentPage, pageSize),
        [totalResultCount, currentPage, pageSize],
    )

    const currentPageStartIndex = Math.max(currentPage - 1, 0) * pageSize
    const currentPagePersons = personsByPage[currentPage] || []
    const paginatedSearchResults = searchResults.slice(
        currentPageStartIndex,
        currentPageStartIndex + pageSize,
    )
    const personsToDisplay = isUsingSearch
        ? paginatedSearchResults
        : currentPagePersons
    const isLoading = isPageLoading || isSearchLoading

    useEffect(() => {
        if (!isUsingSearch) {
            setNonSearchPage((prev) =>
                prev === currentPage ? prev : currentPage,
            )
        }
    }, [currentPage, isUsingSearch])

    useEffect(() => {
        const maxPage = totalPages === 0 ? 1 : totalPages
        const safePage = Math.min(Math.max(currentPage, 1), maxPage)
        if (safePage !== currentPage) {
            setCurrentPage(safePage)
        }
    }, [totalPages, currentPage])

    useEffect(() => {
        if (isUsingSearch && !wasUsingSearchRef.current) {
            setCurrentPage(1)
        } else if (!isUsingSearch && wasUsingSearchRef.current) {
            const maxPage = totalPages === 0 ? 1 : totalPages
            const targetPage = Math.min(Math.max(nonSearchPage, 1), maxPage)
            setCurrentPage(targetPage)
        }

        wasUsingSearchRef.current = isUsingSearch
    }, [isUsingSearch, nonSearchPage, totalPages])

    const selectedIdsSet = useMemo(
        () => new Set(selectedPersonIds),
        [selectedPersonIds],
    )

    const removePersonFromLocalState = useCallback((personId: string) => {
        setAvailableNetworkLinkIds((prev) => {
            const updated = prev.filter((id) => id !== personId)
            return updated.length === prev.length ? prev : updated
        })

        setPersonsByPage((prev) => {
            let didChange = false
            const updated: Record<number, PersonLiteDto[]> = {}
            for (const [pageKey, persons] of Object.entries(prev)) {
                const filtered = persons.filter((p) => p.id !== personId)
                updated[Number(pageKey)] = filtered
                if (filtered.length !== persons.length) {
                    didChange = true
                }
            }
            return didChange ? updated : prev
        })

        setSearchResults((prev) => {
            const filtered = prev.filter((p) => p.id !== personId)
            return filtered.length === prev.length ? prev : filtered
        })
    }, [])

    const remainingSelectableCount = useMemo(() => {
        if (availableNetworkLinkIds.length === 0) return 0
        return availableNetworkLinkIds.reduce((count, id) => {
            return selectedIdsSet.has(id) ? count : count + 1
        }, 0)
    }, [availableNetworkLinkIds, selectedIdsSet])

    const fetchPersonsForPage = useCallback(
        async (pageNumber: number, ids: string[], size: number) => {
            const startIndex = (pageNumber - 1) * size
            const pageIds = ids.slice(startIndex, startIndex + size)
            if (pageIds.length === 0) {
                return []
            }

            const response = await personApi.getPersonsLite({
                personIds: pageIds,
                includeCurrentJobDetails: true,
            })
            // Keep display order consistent with the sliced id list
            const personMap = new Map(response.persons.map((p) => [p.id, p]))
            return pageIds
                .map((id) => personMap.get(id))
                .filter((person): person is PersonLiteDto => Boolean(person))
        },
        [],
    )

    useEffect(() => {
        let isMounted = true
        async function fetchAvailableNetworkLinks() {
            setIsPageLoading(true)
            setPersonsByPage({})
            setSelectedPersonIds([])
            setSearchTerm('')
            setSearchResults([])
            setIsSearchLoading(false)
            let didSucceed = false
            try {
                const availableNetworkLinksIds = (
                    await tenantApi.getAvailableSeeds()
                ).availableSeedIds

                const unselectedAvailableNetworkLinksIds =
                    availableNetworkLinksIds.filter(
                        (id) => !initialNetworkLinksIdsRef.current.includes(id),
                    )

                if (isMounted) {
                    setAvailableNetworkLinkIds(
                        unselectedAvailableNetworkLinksIds,
                    )
                    didSucceed = true
                }
            } catch {
                if (isMounted) {
                    setAvailableNetworkLinkIds([])
                }
            } finally {
                if (isMounted && !didSucceed) {
                    setIsPageLoading(false)
                }
            }
        }
        fetchAvailableNetworkLinks()

        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        if (availableNetworkLinkIds.length === 0) {
            setPersonsByPage({})
            setCurrentPage(1)
            setIsPageLoading(false)
            return
        }

        let cancelled = false
        async function loadCurrentPage() {
            setIsPageLoading(true)
            const totalPagesForIds = Math.max(
                1,
                Math.ceil(availableNetworkLinkIds.length / pageSize),
            )
            const targetPage = Math.min(
                Math.max(currentPageRef.current, 1),
                totalPagesForIds,
            )
            try {
                const persons = await fetchPersonsForPage(
                    targetPage,
                    availableNetworkLinkIds,
                    pageSize,
                )
                if (cancelled) return
                setPersonsByPage({ [targetPage]: persons })
                setCurrentPage((prev) =>
                    prev === targetPage ? prev : targetPage,
                )
            } finally {
                if (!cancelled) {
                    setIsPageLoading(false)
                }
            }
        }
        loadCurrentPage()

        return () => {
            cancelled = true
        }
    }, [availableNetworkLinkIds, pageSize, fetchPersonsForPage])

    useEffect(() => {
        const trimmed = normalizedSearchTerm
        if (!trimmed || availableNetworkLinkIds.length === 0) {
            setSearchResults([])
            setIsSearchLoading(false)
            lastSearchTermRef.current = trimmed
            return
        }

        if (lastSearchTermRef.current !== trimmed) {
            setCurrentPage(1)
            lastSearchTermRef.current = trimmed
        }

        let isCancelled = false
        setIsSearchLoading(true)
        setSearchResults([])

        const timeoutId = setTimeout(async () => {
            try {
                const response = await personApi.searchPersons(trimmed)
                if (isCancelled) return

                const filteredPersons = response.persons
                    .filter((person) => availableIdsSet.has(person.id))
                    .map<PersonLiteDto>((person) => ({
                        id: person.id,
                        firstName: person.firstName,
                        middleName: person.middleName,
                        lastName: person.lastName,
                        dataProviderId: person.dataProviderId,
                        linkedInUrl: person.linkedInUrl,
                        linkedInProfilePicUrl: person.linkedInProfilePicUrl,
                        currentJobDetails: person.currentJobDetails,
                    }))
                filteredPersons.sort(comparePersonsByName)

                setSearchResults(filteredPersons)
            } finally {
                if (!isCancelled) {
                    setIsSearchLoading(false)
                }
            }
        }, 300)

        return () => {
            isCancelled = true
            clearTimeout(timeoutId)
        }
    }, [normalizedSearchTerm, availableNetworkLinkIds, availableIdsSet])

    function handleSelectPersonChanged(
        person: PersonLiteDto,
        selected: boolean,
    ) {
        const personId = person.id

        setSelectedPersonIds((prev) => {
            if (selected) {
                if (prev.includes(personId)) return prev
                return [...prev, personId]
            }
            return prev.filter((id) => id !== personId)
        })

        if (selected) {
            removePersonFromLocalState(personId)
        }
    }

    async function addToNetworkLinks(addAll = false) {
        if (addAll) {
            onClose(availableNetworkLinkIds, true)
        } else {
            onClose(selectedPersonIds, true)
        }
        return
    }

    async function goToPage(newPage: number) {
        if (newPage === currentPage) return
        if (newPage < 1 || totalPages === 0) return
        if (newPage > totalPages) return

        setCurrentPage(newPage)

        if (isUsingSearch) {
            return
        }

        if (personsByPage[newPage]) {
            return
        }

        setIsPageLoading(true)
        try {
            const persons = await fetchPersonsForPage(
                newPage,
                availableNetworkLinkIds,
                pageSize,
            )
            setPersonsByPage((prev) => ({
                ...prev,
                [newPage]: persons,
            }))
        } finally {
            setIsPageLoading(false)
        }
    }

    function handlePageSizeChange(newSize: number) {
        if (newSize === pageSize) return
        if (isUsingSearch) {
            setCurrentPage(1)
            setPageSize(newSize)
            return
        }
        setIsPageLoading(true)
        setPersonsByPage({})
        setCurrentPage(1)
        setPageSize(newSize)
    }

    function handleOnClose() {
        onClose(selectedPersonIds, true)
    }

    const handleAddFromDepartments = useCallback(async () => {
        setDepartmentAddNote(null)

        if (selectedDepartments.length === 0) {
            toast.error('Select at least one department before adding people.')
            return
        }

        if (availableNetworkLinkIds.length === 0) {
            toast('No additional people are available to add.')
            return
        }

        const normalizedDepartments = Array.from(
            new Set(
                selectedDepartments
                    .map(normalizeDepartmentValue)
                    .filter((value): value is string => Boolean(value)),
            ),
        )

        if (normalizedDepartments.length === 0) {
            toast.error('Those departments cannot be translated right now.')
            return
        }

        setIsDepartmentLoading(true)
        try {
            const response =
                await tenantApi.getAvailableSeedPersonIdsByDepartments({
                    departments: normalizedDepartments,
                })

            const matchingIds = response.matchingPersonIds

            if (matchingIds.length === 0) {
                toast(
                    'No people in that department are currently available to add.',
                )
                return
            }

            const departmentSummary = selectedDepartments.join(', ')
            const personLabel = matchingIds.length === 1 ? 'person' : 'people'
            const successMessage = departmentSummary
                ? `Added ${matchingIds.length} ${personLabel} from ${departmentSummary}.`
                : `Added ${matchingIds.length} ${personLabel}.`

            if (onAddFromDepartments) {
                await onAddFromDepartments(matchingIds)
            }

            for (const id of matchingIds) {
                removePersonFromLocalState(id)
            }

            setSelectedDepartments([])
            setDepartmentAddNote(successMessage)
            toast.success(successMessage)
        } catch (error) {
            console.error('Failed to load department people', error)
            toast.error(
                'Unable to load people for that department right now. Please try again.',
            )
        } finally {
            setIsDepartmentLoading(false)
        }
    }, [
        availableNetworkLinkIds,
        onAddFromDepartments,
        removePersonFromLocalState,
        selectedDepartments,
    ])

    return (
        <SelectPersonModal
            persons={personsToDisplay}
            title="Add People to your Trusted Circle"
            onClose={handleOnClose}
            onSelectChanged={(person, selected) => {
                handleSelectPersonChanged(person, selected)
                onLiveSelectChange(person, selected)
            }}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            multiSelectButtons={
                <div className="flex flex-col gap-4">
                    <Note type={NoteType.INFO}>
                        Add people you trust to vouch for candidates you find
                    </Note>
                    <div className="space-y-2">
                        <div className="text-xs font-semibold text-gray-700">
                            Add people by department
                        </div>
                        <div className="flex gap-2 items-center">
                            <TagsInput
                                value={selectedDepartments}
                                validValues={JOB_FUNCTION_VALUES}
                                onChange={(nextTags) => {
                                    setDepartmentAddNote(null)
                                    setSelectedDepartments(nextTags as string[])
                                }}
                                placeholder="Select departments"
                                allowCustomValues={false}
                                renderTag={(tag, { onRemove }) => (
                                    <JobFunctionBadge
                                        text={tag}
                                        showRemove
                                        onRemove={onRemove}
                                    />
                                )}
                            />
                            <Button
                                style="secondary"
                                type="button"
                                size="small"
                                enabled={
                                    selectedDepartments.length > 0 &&
                                    !isDepartmentLoading &&
                                    availableNetworkLinkIds.length > 0
                                }
                                onClick={() => void handleAddFromDepartments()}
                            >
                                Add
                            </Button>
                        </div>
                    </div>
                    {departmentAddNote && (
                        <Note
                            type={NoteType.INFO}
                            containerClassNames="text-left"
                        >
                            {departmentAddNote}
                        </Note>
                    )}
                    <Button
                        style="secondary"
                        type="button"
                        onClick={() => addToNetworkLinks(true)}
                        enabled={remainingSelectableCount > 0}
                    >
                        Add All {remainingSelectableCount} to Trusted Network
                    </Button>
                    {availableNetworkLinkIds.length > 0 && isUsingSearch && (
                        <div className="text-sm">
                            Found {searchResults.length} matching{' '}
                            {searchResults.length === 1 ? 'person' : 'people'}
                        </div>
                    )}
                </div>
            }
            paginationControls={
                totalResultCount > 0 && (
                    <div className="mt-2 w-full">
                        <PaginationControls
                            totalItems={totalResultCount}
                            currentPage={currentPage}
                            onPageChange={goToPage}
                            pageSize={pageSize}
                            defaultPageSize={DEFAULT_PAGE_SIZE}
                            onPageSizeChange={handlePageSizeChange}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                            isLoading={isLoading}
                            textSizeClassName="text-sm"
                        />
                    </div>
                )
            }
            isLoading={isLoading}
        />
    )
}

export default AddNetworkLinksModal
